import { Shield, TrendingUp, Award, Sparkles, AlertTriangle } from "lucide-react";

const TIER_META = {
  trusted: {
    label: "Trusted Sportsman",
    icon: Award,
    color: "hsl(158 48% 21%)",
    bg: "bg-primary/10",
    ring: "border-primary/40",
    text: "text-primary",
    desc: "High activity, clean record.",
  },
  established: {
    label: "Established",
    icon: TrendingUp,
    color: "hsl(34 40% 53%)",
    bg: "bg-accent/10",
    ring: "border-accent/40",
    text: "text-accent",
    desc: "Solid history on the platform.",
  },
  getting_started: {
    label: "Getting Started",
    icon: Sparkles,
    color: "hsl(12 55% 51%)",
    bg: "bg-secondary/10",
    ring: "border-secondary/40",
    text: "text-secondary",
    desc: "Building reputation.",
  },
  new: {
    label: "New Member",
    icon: Sparkles,
    color: "hsl(135 15% 37%)",
    bg: "bg-muted",
    ring: "border-border",
    text: "text-muted-foreground",
    desc: "Just joined the community.",
  },
};

export const TrustBadge = ({ trust, compact = false }) => {
  if (!trust) return null;
  const meta = TIER_META[trust.tier] || TIER_META.new;
  const Icon = meta.icon;

  if (compact) {
    return (
      <span
        className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-2 py-1 rounded border ${meta.ring} ${meta.bg} ${meta.text}`}
        title={`${meta.label} — score ${trust.score}`}
        data-testid="trust-badge-compact"
      >
        <Icon className="w-3 h-3" /> {meta.label}
      </span>
    );
  }

  const scorePct = Math.min(100, (trust.score / 500) * 100);

  return (
    <div className={`rounded-md border ${meta.ring} ${meta.bg} p-5`} data-testid="trust-score-card">
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-md flex items-center justify-center ${meta.text}`} style={{ background: "hsl(0 0% 100% / 0.5)" }}>
          <Icon className="w-5 h-5" />
        </div>
        <div className="flex-1">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Trust</div>
          <div className={`font-display font-extrabold text-lg ${meta.text}`}>{meta.label}</div>
        </div>
        <div className="text-right">
          <div className="font-display font-extrabold text-3xl" style={{ color: meta.color }}>{trust.score}</div>
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">/ 500</div>
        </div>
      </div>

      <div className="mt-4 h-1.5 w-full bg-background rounded-full overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-700"
          style={{ width: `${scorePct}%`, background: meta.color }}
        />
      </div>

      <div className="grid grid-cols-2 gap-3 mt-4 text-sm">
        <Stat label="Sold" value={trust.sold_count} />
        <Stat label="Listings" value={trust.active_listings} />
        <Stat label="Threads" value={trust.threads_count} />
        <Stat label="Days here" value={trust.days_on_platform} />
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        {trust.has_firearms_pass && (
          <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-2 py-1 rounded border border-secondary/40 bg-secondary/10 text-secondary" data-testid="trust-pass-chip">
            <Shield className="w-3 h-3" /> 2A Pass holder
          </span>
        )}
        {trust.reports_count > 0 && (
          <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-2 py-1 rounded border border-destructive/40 bg-destructive/10 text-destructive" data-testid="trust-reports-chip">
            <AlertTriangle className="w-3 h-3" /> {trust.reports_count} report{trust.reports_count > 1 ? "s" : ""} on record
          </span>
        )}
      </div>
    </div>
  );
};

const Stat = ({ label, value }) => (
  <div>
    <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
    <div className="font-semibold text-foreground text-lg">{value}</div>
  </div>
);
